import type { Objective, ObjectiveOrDraft, PillarSummary } from '@/types/objectives';

import { PillarStatus } from '@/constants/PillarStatus';
import { summarizeObjectives } from '@/utils/objectivesSummary';
import { isPublishedObjective } from '@/utils/objectiveStatus';

/** An objective, and one of its holders the pillars are counted among. */
export interface HeldObjective {
    holderId: string;
    objective: ObjectiveOrDraft;
}

/**
 * A pillar's figures counted among some of the people: a published objective serving the pillar
 * counts once as soon as one of its holders is among them, however many are; the participants are
 * those holders; the progression follows the module's one rule (`summarizeObjectives`), the
 * server's own. A draft counts nowhere.
 *
 * The objectives are the ones the reader's list already holds, in the chosen period: nothing counted
 * here is anything the reader could not open.
 */
export const summarizePillarAmong = (
    pillar: PillarSummary,
    heldObjectives: HeldObjective[],
): PillarSummary => {
    const serving = heldObjectives.filter(
        (held): held is HeldObjective & { objective: Objective } =>
            isPublishedObjective(held.objective) && held.objective.pillarIds.includes(pillar.id),
    );
    const objectives = [
        ...new Map(serving.map(({ objective }) => [objective.id, objective])).values(),
    ];

    return {
        ...pillar,
        objectivesCount: objectives.length,
        participantsCount: new Set(serving.map(({ holderId }) => holderId)).size,
        progression: summarizeObjectives(objectives).overall,
    };
};

/**
 * Every pillar of `catalog`, in its order, counted as `summarizePillarAmong` counts one. A pillar no
 * longer active is left out when none of these objectives serves it, as the server leaves it out.
 */
export const pillarSummariesAmong = (
    catalog: PillarSummary[],
    heldObjectives: HeldObjective[],
): PillarSummary[] =>
    catalog
        .map((pillar) => summarizePillarAmong(pillar, heldObjectives))
        .filter((pillar) => pillar.status === PillarStatus.ACTIVE || pillar.objectivesCount > 0);
